/**
 * „Amit eddig feltöltöttem" — nyugta ÉS visszakeresés.
 *
 * MIÉRT KELL EGYÁLTALÁN. A `§8c` 8. lépése azt mondja, a dokumentum
 * „tárolódik ÉS LÁTSZIK". Enélkül a feltöltés fekete lyuk, és az ügyfél a
 * bizonytalanságtól tölti fel másodszor is ugyanazt — pont azt a viselkedést
 * termelve, ami ellen a `2.6/20` íródott.
 *
 * A LAPOZÁS AZÉRT VAN, MERT A RÉGI FÁJL IS KELL *(Viktor, 2026-09-04)*.
 * Korábban húsz sor jött, és ott véget ért — havi 300 számlás ügyfélnél az
 * hetek alatt elfogy, és onnantól a saját archívuma némán csonka. A
 * „Régebbiek" nem új képernyő: ugyanaz a szakasz, ugyanazon a munkaképernyőn.
 *
 * A HIBA ITT NEM ÁLLÍTJA MEG A KÉPERNYŐT. A kérések listája a fő tartalom; ha
 * a nyugta nem jön be, azt a szakaszt hagyjuk el, nem az egész oldalt.
 *
 * A nemzedék-számlálóról lásd a `useRequests.ts` fejlécét — ugyanaz az érv.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api-client';
import type { ClientDocType, UploadedDocument, UploadPage } from './types';

/**
 * AZ OLVASÁS UTÁN MAGÁTÓL FRISSÜL A SOR (#84) — ilyen ütemben.
 *
 * A kinyerés pár másodperc; az első percben ezért sűrűn kérdezünk. Ha addig
 * sem végzett, a sor a söprögetőre vár (visszalépő újrapróbálás, akár
 * percekig) — onnan ritkán kérdezünk, hogy egy nyitva felejtett telefon ne
 * hívja a szervert háromszor percenként egy beragadt sorért.
 */
export const READING_POLL_FAST_MS = 4_000;
export const READING_POLL_SLOW_MS = 30_000;
const READING_FAST_POLLS = 15;

/** Ami az újrakérdezéskor ÁTÍRÓDHAT egy soron — a kiolvasás, és semmi más. */
function withReading(upload: UploadedDocument, fresh: UploadedDocument): UploadedDocument {
  return {
    ...upload,
    reading: fresh.reading,
    partnerName: fresh.partnerName,
    invoiceNumber: fresh.invoiceNumber,
    grossAmount: fresh.grossAmount,
    currency: fresh.currency,
  };
}

/**
 * A VÁLASZ ALAKJÁT NEM HISSZÜK EL, PEDIG A TÍPUS AZT ÁLLÍTJA.
 *
 * A `UploadPage` a HÁLÓZATRÓL jön: egy régebbi backend, egy félbement válasz
 * vagy egy elrontott DTO tömböt ad objektum helyett, és azt a típusrendszer
 * NEM fogja meg. Kezeletlenül a `page.items` `undefined` lesz, a rá futó
 * `.length` pedig `TypeError`-t dob RENDER KÖZBEN — vagyis az EGÉSZ
 * munkaképernyő fehérre megy egy MELLÉKES szakasz miatt. A nyugta nem a
 * munka kapuja; a kérések listája a fő tartalom.
 *
 * *(Nem elméleti: pontosan ez történt, amikor a végpont egy lapozott alakra
 * váltott, és a tesztek régi fixture-e még tömböt adott — 2026-09-04.)*
 *
 * Ugyanaz a fogás, mint az admin `embedKind`-jánál: a hiányzó mező a
 * felületet nem viheti el.
 */
function asPage(received: UploadPage): UploadPage {
  return {
    items: Array.isArray(received?.items) ? received.items : [],
    hasMore: received?.hasMore === true,
    // A HIÁNYZÓ MEZŐ `null`, NEM ÜRES LISTA: az üres lista azt állítaná, hogy a
    // könyvelő mindent letiltott, és az ügyfél egyetlen választást sem látna.
    docTypes: Array.isArray(received?.docTypes) ? received.docTypes : null,
  };
}

export interface UploadsState {
  uploads: UploadedDocument[];
  hasMore: boolean;
  /** Fut-e ÉPP egy „Régebbiek" — a gomb ettől tiltódik le. */
  loadingMore: boolean;
  /** A cég engedélyezett típusai — lásd `UploadPage.docTypes`. */
  docTypes: ClientDocType[] | null;
  reload: () => void;
  loadMore: () => void;
  /**
   * A típusválasz HELYBEN átvezetve, újratöltés nélkül.
   *
   * MIÉRT NEM `reload()`. Az újratöltés az ELSŐ lapra ugrana vissza — ha az
   * ügyfél addig lelapozott a régi fájljaihoz, pont azt veszítené el, amiért
   * odament. Egy `PATCH` egy sort érint, tehát egy sort írunk át.
   */
  markDeclared: (documentId: string, docType: ClientDocType) => void;
}

export function useUploads(): UploadsState {
  const [uploads, setUploads] = useState<UploadedDocument[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [docTypes, setDocTypes] = useState<ClientDocType[] | null>(null);
  const generation = useRef(0);

  const reload = useCallback(() => {
    generation.current += 1;
    const mine = generation.current;

    void api<UploadPage>('/documents')
      .then((received) => {
        if (mine === generation.current) {
          const page = asPage(received);
          // AZ ELSŐ LAP FELÜLÍR, NEM HOZZÁFŰZ. A `reload` egy friss feltöltés
          // után fut: ha itt hozzáfűznénk, a most beérkezett bizonylat a már
          // betöltött régebbi lapok MELLÉ kerülne, és ugyanaz a sor kétszer
          // látszana.
          setUploads(page.items);
          setHasMore(page.hasMore);
          // AZ ENGEDÉLY CSAK AZ ELSŐ LAPRÓL ÍRÓDIK: a „Régebbiek" lapja
          // ugyanazt hozza, és egy elkésett régi lap ne írja felül a frisset.
          setDocTypes(page.docTypes);
        }
      })
      .catch(() => {
        // Szándékosan néma: lásd a fájl fejlécét.
      });
  }, []);

  const loadMore = useCallback(() => {
    const last = uploads[uploads.length - 1];
    if (last === undefined || loadingMore) {
      return;
    }

    generation.current += 1;
    const mine = generation.current;
    setLoadingMore(true);

    void api<UploadPage>(`/documents?after=${encodeURIComponent(last.id)}`)
      .then((received) => {
        if (mine !== generation.current) {
          return;
        }
        const page = asPage(received);
        // A HOZZÁFŰZÉS A FRISS ÁLLAPOTRA MEGY, nem a záródásban rekedt
        // `uploads`-ra: két gyors kattintás közt a lista már mozdulhatott.
        setUploads((current) => [...current, ...page.items]);
        setHasMore(page.hasMore);
      })
      .catch(() => {
        // ⚠️ A `hasMore` BENT MARAD. Egy elbukott lapozás nem bizonyítja,
        // hogy nincs több — ha itt `false`-ra váltanánk, a gomb eltűnne, és
        // az ügyfél azt hinné, ennyi az archívuma. Így a gomb marad, és újra
        // lehet próbálni.
      })
      .finally(() => {
        if (mine === generation.current) {
          setLoadingMore(false);
        }
      });
  }, [uploads, loadingMore]);

  const markDeclared = useCallback((documentId: string, docType: ClientDocType) => {
    setUploads((current) =>
      current.map((upload) =>
        upload.id === documentId ? { ...upload, clientDocType: docType } : upload
      )
    );
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  /**
   * AMÍG VAN OLVASOTT SOR, ÚJRAKÉRDEZÜNK (#84).
   *
   * ⚠️ NEM `reload()`. Az az első lapra írja vissza a listát, és egy
   * „Régebbiek" után az ügyfél a lelapozott sorait veszítené el — négy
   * másodpercenként. Itt az első lapot kérjük, és CSAK a kiolvasás mezőit
   * írjuk át a már ott álló sorokon, azonosító szerint. A `clientDocType`-hoz
   * sem nyúlunk: egy közben elküldött „Mi ez?" válasz ne vesszen el egy
   * korábban indult kérés miatt.
   *
   * A `tick` AZÉRT KELL, mert egy elbukott kérés után a lista nem változik —
   * ha csak a sorokra figyelnénk, az első hálózati hiba végleg leállítaná.
   */
  const reading = uploads.some((upload) => upload.reading === 'READING');
  const [tick, setTick] = useState(0);
  const polls = useRef(0);

  useEffect(() => {
    if (!reading) {
      polls.current = 0;
      return;
    }
    void tick;
    const delay = polls.current < READING_FAST_POLLS ? READING_POLL_FAST_MS : READING_POLL_SLOW_MS;
    const timer = setTimeout(() => {
      polls.current += 1;
      // A NEMZEDÉK ITT IS ŐR: egy közben lefutott `reload()` frissebb sorokat
      // hozott, és egy előtte indult újrakérdezés ne írja vissza őket régire.
      // A `tick` ettől még lép (`finally`), tehát a kör nem áll le.
      const mine = generation.current;
      void api<UploadPage>('/documents')
        .then((received) => {
          if (mine !== generation.current) {
            return;
          }
          const fresh = new Map(asPage(received).items.map((item) => [item.id, item]));
          setUploads((current) =>
            current.map((upload) => {
              const next = fresh.get(upload.id);
              return next === undefined ? upload : withReading(upload, next);
            })
          );
        })
        .catch(() => {
          // Néma, mint a lista többi kérése; a következő kör újrapróbálja.
        })
        .finally(() => setTick((value) => value + 1));
    }, delay);

    return () => clearTimeout(timer);
  }, [reading, tick]);

  return { uploads, hasMore, loadingMore, docTypes, reload, loadMore, markDeclared };
}
